import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import {
  createProfileReport,
  createProfileReportFinished,
  createProfileReportStarted
} from '@capital-access/common/actions';
import { getFeatureToggle } from '@capital-access/common/feature-toggle';
import {
  getUserSettingsCulture,
  getUserSettingsCurrency,
  getUserSettingsLanguage,
  getUserSettingsTimezone
} from '@capital-access/common/globalization';
import { LocalizationService } from '@capital-access/common/localization';
import {
  getSelectedRelatedSecurities,
  getSelectedSecurity,
  getSelectedSecuritySurveillancePermission,
  Security
} from '@capital-access/common/security';
import { DEFAULT_TEMPLATE_KEY, ModuleName } from '@capital-access/common/utils';
import { NotificationService } from '@capital-access/firefly/components';
import { getDefaultSecurityListSettings } from '@capital-access/lists/common';
import { ProfileType } from '@capital-access/profiles/common';
import {
  createConfigurableProfileReport,
  INCREASE_EXPORT_LIMIT_FT,
  MAX_REPORT_PROFILES_COUNT,
  MAX_REPORT_PROFILES_COUNT_WITH_FT,
  MAX_SMALL_REPORT_ITEMS_COUNT,
  OutputFormat,
  TemplateLibraryService
} from '@capital-access/reporting/common';
import { LOCALIZATION_KEY } from '../../constants';
import { generateReport } from '../../core/+state/report.actions';
import { OwnershipSource } from '../../core/models/ownership-source.enum';
import { ReportSection } from '../../core/models/report-sections';
import { getValidConfigurableLayout } from '../../core/utils/configurable-template.util';
import { getSavedSections } from '../../core/utils/section-settings.utils';
import * as reportActions from '../+state/profile-report.actions';
import { CreateProfileReportDto, CreateProfileReportRequest } from '../models/profile-report.models';
import {
  getConditionalReportSections,
  getDefaultSections,
  ProfileReportLayout
} from '../models/profile-report-sections';
import { ProfileReportNamingService } from '../services/profile-report-naming.service';
import { SortingDirectionService } from '../services/sorting-direction.service';

@Injectable()
export class ProfileReportEffects {
  launchProfileReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createProfileReport),
      withLatestFrom(
        this.store.select(getSelectedRelatedSecurities),
        this.store.select(getFeatureToggle('profile-report-historical-ownership-SKYL-2377')),
        this.store.select(getFeatureToggle('profile-report-sustainability-fit-ISSYN-2489')),
        this.store.select(getFeatureToggle('add-notes-section-in-investor-profile-report-ISD-17482')),
        this.store.select(getFeatureToggle('ai-summary-card-isd-4584'))
      ),
      switchMap(
        ([
          action,
          relatedSecurities,
          isHistoricalOwnershipToggleEnabled,
          isSustainabilityFitToggleEnabled,
          isNotesFeatureEnabled,
          isAiSummaryCardEnabled
        ]) => {
          return getValidConfigurableLayout(this.store, this.templateService, action.profileType).pipe(
            map(layout =>
              this.createReport(
                action,
                layout,
                relatedSecurities,
                isHistoricalOwnershipToggleEnabled,
                isSustainabilityFitToggleEnabled,
                isNotesFeatureEnabled,
                isAiSummaryCardEnabled,
                action.outputFormat
              )
            )
          );
        }
      )
    )
  );

  private createReport(
    action: any,
    layout: ProfileReportLayout,
    relatedSecurities: Security[],
    isHistoricalOwnershipToggleEnabled: boolean,
    isSustainabilityFitToggleEnabled: boolean,
    isNotesFeatureEnabled: boolean,
    isAiSummaryCardEnabled: boolean,
    outputFormat?: OutputFormat
  ) {
    if (layout == ProfileReportLayout.Configurable) {
      return createConfigurableProfileReport({
        profiles: action.profiles,
        profileType: action.profileType === ProfileType.Institution ? 'institution' : 'contact',
        templateId: Number(
          localStorage.getItem(
            `${
              action.profileType === ProfileType.Institution ? 'institution' : 'contact'
            }_${DEFAULT_TEMPLATE_KEY}`.toUpperCase()
          ) ?? 0
        ),
        moduleName:
          action.profileType === ProfileType.Institution ? ModuleName.InstitutionProfile : ModuleName.ContactProfile,
        reportedQuartersFilter: (action.profiles || []).map((p: { prId?: string; crmId?: string }) => ({
          profileId: `${p.prId ?? ''}|${p.crmId ?? ''}`,
          reportedQuarters: action!.reportedQuarters ?? []
        })),
        outputFormat
      });
    } else {
      const conditionalSections = getConditionalReportSections({
        historicalOwnership: action.profileType !== ProfileType.Contact || isHistoricalOwnershipToggleEnabled,
        sustainabilityFitMetric: action.profileType === ProfileType.Institution && isSustainabilityFitToggleEnabled,
        primaryContactBiography: action.profileType === ProfileType.Institution,
        participantBiography: false,
        combinedOwnership: action.profileType === ProfileType.Institution && relatedSecurities?.length >= 2
      });
      const defaultSection = getDefaultSections(
        action.profileType as ProfileType,
        layout,
        isNotesFeatureEnabled,
        isAiSummaryCardEnabled
      );

      let sections: ReportSection[];

      if (!action.useCustomSettings) {
        sections = [];
      } else {
        const key =
          layout === ProfileReportLayout.OnePager
            ? `${action.profileType}${ProfileReportLayout.OnePager}`
            : action.profileType;

        sections = getSavedSections(key, defaultSection, conditionalSections)
          .filter(s => s.isSelected)
          .map(s => s.id);
      }

      return reportActions.createProfileReport({
        request: CreateProfileReportDto.create({
          profiles: action.profiles,
          profileType: action.profileType,
          combinedSecurityIds: action.combinedSecurityIds || relatedSecurities.map(x => x.id),
          reportedQuarters: action.reportedQuarters,
          sortDirection: this.sortingDirectionService.getSortDirection()
        }),
        sections,
        layout,
        outputFormat
      });
    }
  }
  createProfileReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reportActions.createProfileReport),
      withLatestFrom(
        this.store.select(getSelectedSecurity),
        this.store.select(getUserSettingsTimezone),
        this.store.select(getFeatureToggle('skyl-2231-culture-specific-formatting')),
        this.store.select(getUserSettingsCulture),
        this.store.select(getUserSettingsLanguage),
        this.store.select(getDefaultSecurityListSettings),
        this.store.select(getSelectedSecuritySurveillancePermission),
        this.store.select(getUserSettingsCurrency),
        this.store.select(getFeatureToggle(INCREASE_EXPORT_LIMIT_FT))
      ),
      mergeMap(
        ([
          { request, sections, layout, outputFormat },
          security,
          timeZone,
          cultureFtEnabled,
          culture,
          language,
          defaultPeerList,
          isSrvPermissioned,
          currency,
          exportReportLimitFtEnabled
        ]) => {
          if (
            !!request.profiles?.length &&
            request.profiles.length > 0 &&
            request.profiles.length <=
              (exportReportLimitFtEnabled ? MAX_REPORT_PROFILES_COUNT_WITH_FT : MAX_REPORT_PROFILES_COUNT)
          ) {
            const profileIds = request.profiles.map(p => p.id.toString());
            const isSrv = security?.surveillance ?? false;
            const reportRequest = this.profileReportNameService
              .getProfileReportFileName(isSrvPermissioned, isSrv, request, timeZone, layout)
              .pipe(
                map(filename => {
                  return this.generateProfileReportRequest(
                    {
                      profileIds,
                      profileType: request.profileType,
                      includedContacts: request.includedContacts,
                      timeZone,
                      culture: cultureFtEnabled ? culture : 'en-US',
                      language: language,
                      securityId: security?.id,
                      combinedSecurityIds: request.combinedSecurityIds,
                      sections,
                      defaultPeerListId: defaultPeerList.id!,
                      fileName: filename,
                      currency,
                      dataSource: isSrv ? OwnershipSource.Surveillance : OwnershipSource.Public,
                      reportedQuarters: request.reportedQuarters,
                      sortDirection: this.sortingDirectionService.getSortDirection(),
                      outputFormat
                    },
                    layout
                  );
                })
              );

            if (request.profiles.length > MAX_SMALL_REPORT_ITEMS_COUNT) {
              return this.localization.localize('waitingTimeMessage', {}, { scope: LOCALIZATION_KEY }).pipe(
                tap(message => this.notificationService.notify(message, { type: 'info' })),
                switchMap(() => reportRequest)
              );
            }

            return reportRequest;
          }

          return of(createProfileReportFinished());
        }
      )
    )
  );

  createProfileReportStarted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createProfileReport, reportActions.createProfileReport, createConfigurableProfileReport),
      map(() => createProfileReportStarted())
    )
  );

  private generateProfileReportRequest(request: CreateProfileReportRequest, layout?: ProfileReportLayout) {
    const successMessage = `${request.profileType}Profile${request.profileIds.length > 1 ? 's' : ''}Created`;
    const failureMessage = `${request.profileType}InvestorProfile${request.profileIds.length > 1 ? 's' : ''}Failed`;

    return generateReport({
      request,
      url:
        layout === ProfileReportLayout.OnePager
          ? 'one-pager-reports/process/batch/launch'
          : 'investor-profile-reports/process/batch/launch',
      successMessage,
      failureMessage,
      finishAction: createProfileReportFinished()
    });
  }

  constructor(
    private actions$: Actions,
    private store: Store,
    private localization: LocalizationService,
    private notificationService: NotificationService,
    private profileReportNameService: ProfileReportNamingService,
    private sortingDirectionService: SortingDirectionService,
    private templateService: TemplateLibraryService,
    @Inject('IS_CA_TEMPLATE') private readonly isCaTemplate: boolean
  ) {}
}
